import React, { memo } from "react";
import { Pagination } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { useApp } from "../../contexts/AppContext";
import "./Pagination.css";

const CustomPagination = ({
  totalItems,
  itemsPerPage = 10,
  currentPage = 1,
  onPageChange,
  maxDisplayedPages = 5,
}) => {
  const {
    device: { isMobile },
  } = useApp();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Don't render pagination if there's only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  const renderPagination = () => {
    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        {isMobile ? (
          <Icon path={mdiChevronLeft} size={0.8} className="flex-shrink-0" />
        ) : (
          "Previous"
        )}
      </Pagination.Prev>
    );

    // For small number of pages, show all pages
    if (totalPages <= 7) {
      for (let number = 1; number <= totalPages; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => onPageChange(number)}
          >
            {number}
          </Pagination.Item>
        );
      }
    } else {
      // Complex pagination for many pages
      // Always show first page
      items.push(
        <Pagination.Item
          key={1}
          active={currentPage === 1}
          onClick={() => onPageChange(1)}
        >
          1
        </Pagination.Item>
      );

      // Show ellipsis after first page if needed
      if (currentPage > 4) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      // Determine range around current page
      let startRange = Math.max(2, currentPage - 1);
      let endRange = Math.min(totalPages - 1, currentPage + 1);

      // Adjust range to show more pages near the beginning or end
      if (currentPage <= 4) {
        startRange = 2;
        endRange = Math.min(5, totalPages - 1);
      } else if (currentPage >= totalPages - 3) {
        startRange = Math.max(totalPages - 4, 2);
        endRange = totalPages - 1;
      }

      // Show pages in range (excluding first and last which are handled separately)
      for (let number = startRange; number <= endRange; number++) {
        items.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => onPageChange(number)}
          >
            {number}
          </Pagination.Item>
        );
      }

      // Show ellipsis before last page if needed
      if (currentPage < totalPages - 3) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      // Always show last page (only if totalPages > 1)
      if (totalPages > 1) {
        items.push(
          <Pagination.Item
            key={totalPages}
            active={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </Pagination.Item>
        );
      }
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        {isMobile ? (
          <Icon path={mdiChevronRight} size={0.8} className="flex-shrink-0" />
        ) : (
          "Next"
        )}
      </Pagination.Next>
    );

    return <Pagination className="custom-pagination">{items}</Pagination>;
  };

  return renderPagination();
};

export default memo(CustomPagination);
